import React, { useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import {
  type EmblaCarouselType as CarouselApi,
  type EmblaEventListType as CarouselEventList,
  type EmblaEventType as CarouselEventModel,
} from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
//img
import img1 from '../..//images/gallery/tamamura-sama-kanami.png';
import img2 from '../../images/gallery/apu-sama-kanami.jpg';
import img3 from '../../images/gallery/a-sama-kanami.jpg';
import img4 from '../../images/gallery/antaro-sama-kanami.jpg';
import img5 from '../../images/gallery/unisuke-sama-kanami.jpg';
import img6 from '../../images/gallery/usamaru-sama-kanami.jpg';
import img7 from '../../images/gallery/kumawo-sama-kanami.jpg';

const TWEEN_FACTOR_BASE = 0.52;
const MIN_SCALE = 0.75;
const MAX_SCALE = 1;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max)

const Gallery = () => {
  const autoplay = useRef(
    Autoplay(
      {
        delay: 4500,          // 3秒ごとにスライド
        stopOnInteraction: false, // ユーザー操作後も止めない
        stopOnMouseEnter: true,   // ホバー中は止める（好み）
      }
    )
  )

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true },
    [autoplay.current]
  )
  const tweenFactor = useRef(0)
  const tweenNodes = useRef<HTMLElement[]>([])

  const slides = [
  { src: img1, title: 'たまむら様'},
  { src: img2, title: 'あぷ。様'},
  { src: img3, title: 'あ様'},
  { src: img4, title: 'Antaro様'},
  { src: img5, title: '雲丹助様'},
  { src: img6, title: 'うさまる様'},
  { src: img7, title: 'くまを様'},
];

  const setTweenNodes = useCallback((emblaApi: CarouselApi): void => {
    tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
      return slideNode.querySelector('.embla__slide__media') as HTMLElement
    })
  }, [])

  const setTweenFactor = useCallback((emblaApi: CarouselApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const tweenScale = useCallback((
    emblaApi: CarouselApi,
    event?: CarouselEventModel | 'reinit' | 'slideFocus'
  ) => {
    const engine = emblaApi.internalEngine()
    const scrollProgress = emblaApi.scrollProgress()
    const slidesInView = emblaApi.slidesInView()
    const isScrollEvent = event === 'scroll'

    emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
      let diffToTarget = scrollSnap - scrollProgress
      const slidesInSnap = engine.slideRegistry[snapIndex]

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            if (slideIndex === loopItem.index && loopItem.target() !== 0) {
              const sign = Math.sign(loopItem.target())
              if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress)
              if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress)
            }
          })
        }
        // 👇 中央との差を元にスケール算出
        const distance = Math.abs(diffToTarget * tweenFactor.current)
        const scale = numberWithinRange(
          MAX_SCALE - distance,
          MIN_SCALE,
          MAX_SCALE
        );
        const tweenNode = tweenNodes.current[slideIndex];
        if (tweenNode) {
          tweenNode.style.transform = `scale(${scale})`;
        }
      });
    });
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    // 初期実行
    setTweenNodes(emblaApi)
    setTweenFactor(emblaApi)
    tweenScale(emblaApi)

    // イベント登録
    emblaApi
      .on('reinit' as any, (api) => {
        setTweenNodes(api)
        setTweenFactor(api)
        // 第2引数に明示的に 'reinit' を渡す
        tweenScale(api, 'reinit')
      })
      .on('scroll', (api, event) => {
        // v8では event (string) が直接渡される
        tweenScale(api, event)
      })
      .on('slideFocus', (api, event) => {
        tweenScale(api, event)
      })

    // クリーンアップ（イベントの解除は v8 では destroy 時に自動で行われますが、
    // useEffect の再実行に備えるなら明示的な解除は不要な設計が多いです）
  }, [emblaApi, setTweenNodes, setTweenFactor, tweenScale])

  return (
    <div className='content-wrap'>
      <div className='title'>-GALLERY-</div>
      <div className="embla">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {slides.map((slide, index) => (
              <div className="embla__slide" key={index}>
                <div className="embla__slide__media">
                  <div className="img-caption">{slide.title}</div>
                  <div className='img-wrap'>
                    <img className="img" src={slide.src} alt={"kanami-illust-" + (index + 1)}/>
                    <div className="embla__floor-shadow" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Gallery;